// Commission rates that replace POS card surcharges once Adyen removes them (midnight 1 Oct 2026).
// The server keeps them on `restaurant.adyenPaymentSettings` and turns them into the merchant's own
// Adyen split profile; these helpers only shape the form and describe what is stored.

export const MAX_RATE_PERCENT = 10;
export const MAX_EFTPOS_CENTS = 500;
export const DEFAULT_EFTPOS_CENTS = 30;

export const COMMISSION_FIELDS = [
  { key: "domesticRate", label: "Domestic cards (%)" },
  { key: "internationalRate", label: "International cards (%)" },
  { key: "amexDomesticRate", label: "Amex domestic (%)" },
];

// How EFTPOS is charged. With "domestic" the profile has no EFTPOS rule, so EFTPOS payments take the
// domestic rate.
export const EFTPOS_MODES = [
  { value: "fixed", label: "Fixed fee", fieldLabel: "EFTPOS fixed fee (cents)" },
  { value: "percentage", label: "Percentage", fieldLabel: "EFTPOS rate (%)" },
  { value: "domestic", label: "Domestic rate" },
];

const isBlank = (value) => value === "" || value === null || value === undefined;

const eftposModeOf = (rates) => {
  if (!isBlank(rates.eftposRate)) return "percentage";
  if (!isBlank(rates.eftposAmount)) return "fixed";
  return "domestic";
};

// A restaurant with no saved rates starts on the standard 30c EFTPOS fee.
export const toFormValues = (commissionRates) => {
  const rates = commissionRates || {};
  const eftposMode = commissionRates ? eftposModeOf(rates) : "fixed";
  let eftposValue = "";
  if (!commissionRates) {
    eftposValue = DEFAULT_EFTPOS_CENTS;
  } else if (eftposMode === "percentage") {
    eftposValue = rates.eftposRate;
  } else if (eftposMode === "fixed") {
    eftposValue = rates.eftposAmount;
  }
  return {
    domesticRate: rates.domesticRate ?? "",
    internationalRate: rates.internationalRate ?? "",
    amexDomesticRate: rates.amexDomesticRate ?? "",
    eftposMode,
    eftposValue,
  };
};

const parsePercentage = (raw) => {
  const value = isBlank(raw) ? NaN : Number(raw);
  return Number.isFinite(value) && value >= 0 && value <= MAX_RATE_PERCENT ? value : null;
};

const parseEftpos = (mode, raw) => {
  if (mode === "domestic") {
    return { rates: { eftposAmount: null, eftposRate: null } };
  }
  if (mode === "percentage") {
    const rate = parsePercentage(raw);
    return rate === null
      ? { error: `EFTPOS rate must be between 0 and ${MAX_RATE_PERCENT}` }
      : { rates: { eftposAmount: null, eftposRate: rate } };
  }
  const cents = isBlank(raw) ? NaN : Number(raw);
  return Number.isInteger(cents) && cents >= 0 && cents <= MAX_EFTPOS_CENTS
    ? { rates: { eftposAmount: cents, eftposRate: null } }
    : { error: `EFTPOS fixed fee must be whole cents between 0 and ${MAX_EFTPOS_CENTS}` };
};

// Returns { rates } ready to send, or { error } naming the first bad field. Zero is a valid rate.
export const parseCommissionForm = (values) => {
  const rates = {};
  for (const { key, label } of COMMISSION_FIELDS) {
    const value = parsePercentage(values[key]);
    if (value === null) {
      return { error: `${label} must be between 0 and ${MAX_RATE_PERCENT}` };
    }
    rates[key] = value;
  }
  const eftpos = parseEftpos(values.eftposMode, values.eftposValue);
  return eftpos.error ? { error: eftpos.error } : { rates: { ...rates, ...eftpos.rates } };
};

// Same rule as the server: the saved rates are live once a profile was applied at or after they were saved.
export const isCommissionApplied = (settings) =>
  Boolean(
    settings &&
      settings.commissionSplitConfigurationId &&
      settings.commissionRatesAppliedAt &&
      settings.commissionRatesUpdatedAt &&
      settings.commissionRatesAppliedAt >= settings.commissionRatesUpdatedAt
  );

export const commissionStatus = (settings) => {
  if (!settings || !settings.commissionRates) {
    return "Not set";
  }
  return isCommissionApplied(settings)
    ? `Applied (${settings.commissionSplitConfigurationId})`
    : "Saved, not applied yet - applies after Adyen removes surcharges at midnight 1 Oct";
};

export const formatCommissionRates = (commissionRates) => {
  if (!commissionRates) {
    return "";
  }
  const { domesticRate, internationalRate, amexDomesticRate, eftposAmount, eftposRate } = commissionRates;
  const modes = {
    percentage: `EFTPOS ${eftposRate}%`,
    fixed: `EFTPOS ${eftposAmount}c`,
    domestic: "EFTPOS at domestic rate",
  };
  return (
    `Domestic ${domesticRate}% · International ${internationalRate}% · ` +
    `Amex domestic ${amexDomesticRate}% · ${modes[eftposModeOf(commissionRates)]}`
  );
};

// A split rule's commission as Adyen stores it: variablePercentage in basis points, fixedAmount in cents.
export const formatSplitCommission = (commission) => {
  const percent = (commission && commission.variablePercentage) || 0;
  const cents = (commission && commission.fixedAmount) || 0;
  const parts = [];
  if (percent || !cents) {
    parts.push(`${(percent / 100).toFixed(2)}%`);
  }
  if (cents) {
    parts.push(`${cents}c`);
  }
  return parts.join(" + ");
};
